/**
 * Definition d'un mot de passe depuis un lien recu par e-mail.
 *
 * Cette page MANQUAIT entierement (constate le 30/08/2026) : le backend
 * envoyait bien un lien vers `/password-reset?token=…`, mais aucune page
 * ne repondait a cette adresse. Le parcours etait donc casse de bout en
 * bout - e-mail envoye, lien mort.
 *
 * Elle sert deux cas :
 *   - un mot de passe oublie ;
 *   - un PREMIER acces, sur un compte cree par l'exploitant. C'est le
 *     seul moyen d'activer un tel compte : son mot de passe initial est
 *     aleatoire et personne ne le connait, volontairement.
 */
import { useState } from "react";

import { ApiError } from "../api";
import { Erreur } from "../components/ui";

/** Le backend impose 12 caracteres minimum (PasswordResetConfirm). On le
 *  verifie ici aussi, pour le dire AVANT l'envoi plutot que de renvoyer
 *  une erreur serveur. */
const LONGUEUR_MINIMALE = 12;

export function PasswordReset({ onTermine }: { onTermine: () => void }) {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const [reussi, setReussi] = useState(false);

  const identiques = motDePasse === confirmation;
  const assezLong = motDePasse.length >= LONGUEUR_MINIMALE;

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      const reponse = await fetch("/api/auth/password-reset/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: motDePasse }),
      });
      if (!reponse.ok) {
        let detail: string | null = null;
        try {
          detail = (await reponse.json())?.detail ?? null;
        } catch {
          /* corps non-JSON */
        }
        throw new ApiError(
          reponse.status === 410 || reponse.status === 404 ? "not_found" : "validation",
          reponse.status,
          detail,
        );
      }
      setReussi(true);
    } catch (e2) {
      setErreur(e2);
    } finally {
      setEnvoi(false);
    }
  };

  if (!token) {
    return (
      <div className="centered" style={{ padding: "2rem 1rem" }}>
        <div className="card stack" style={{ width: "min(28rem, 100%)" }}>
          <h1 className="card__title">Lien incomplet</h1>
          <p>
            Cette adresse ne contient pas de jeton. Ouvrez le lien tel qu'il figure dans
            l'e-mail, sans le modifier.
          </p>
          <div className="actions" style={{ marginTop: 0 }}>
            <button type="button" className="btn btn--secondary" onClick={onTermine}>
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (reussi) {
    return (
      <div className="centered" style={{ padding: "2rem 1rem" }}>
        <div className="card stack" style={{ width: "min(28rem, 100%)" }}>
          <div className="alert alert--success" role="status">
            <strong>Mot de passe enregistré.</strong>
          </div>
          <p>Vous pouvez maintenant vous connecter.</p>
          <div className="actions" style={{ marginTop: 0 }}>
            <button type="button" className="btn btn--primary" onClick={onTermine}>
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="centered" style={{ padding: "2rem 1rem" }}>
      <form className="card stack" style={{ width: "min(28rem, 100%)" }} onSubmit={soumettre}>
        <div>
          <h1 className="card__title" style={{ marginBottom: "0.15rem" }}>
            Définir votre mot de passe
          </h1>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Ce lien est valable une heure et ne sert qu'une fois.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="mdp">
            Nouveau mot de passe
          </label>
          <input
            id="mdp"
            className="input"
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
          />
          <p className="muted" style={{ marginTop: "0.3rem" }}>
            {LONGUEUR_MINIMALE} caractères minimum. Une phrase dont vous vous souvenez vaut
            mieux qu'une suite de symboles que vous devrez noter quelque part.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="conf">
            Confirmation
          </label>
          <input
            id="conf"
            className="input"
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            aria-invalid={confirmation !== "" && !identiques ? "true" : undefined}
            required
          />
          {confirmation !== "" && !identiques && (
            <p className="muted" style={{ marginTop: "0.3rem", color: "var(--red-dark)" }}>
              Les deux saisies diffèrent.
            </p>
          )}
        </div>

        <Erreur erreur={erreur} />

        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={envoi || !assezLong || !identiques}
          >
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}
